'use client';

import 'lib/sitecore/patchPagesPlaceholderHydration';

/**
 * This Layout is needed for Starter Kit.
 */

import { useRef, JSX, useEffect } from 'react';
import ClientHead from 'lib/client-head/ClientHead';
import { tv } from 'tailwind-variants';
import { DesignLibrary, Field, Page } from '@sitecore-content-sdk/nextjs';
import { GoogleTagManager, sendGTMEvent } from '@next/third-parties/google';
import type { XceleratePage } from 'src/baseTypes/Xcelerate.Irwinmitchell.model';
import Scripts from 'src/Scripts';
import SitecoreStyles from 'src/components/content-sdk/SitecoreStyles';
import { PlaceholderWrapper } from 'helpers/SitecoreWrappers/PlaceholderWrapper/PlaceholderWrapper';
import { SiteName } from 'helpers/Constants/Constant';
import { BrandAndThemeProvider, getBrandForSiteName } from 'lib/context/BrandAndThemeContext';
import BackToTop from 'components/authorable/shared/site-structure/BackToTop/BackToTop';
import { useOnRouteChange } from 'lib/hooks/useOnRouteChange';
import { SpeedInsights } from '@vercel/speed-insights/react';
import Schema from 'src/Schema';
import { createContentViewGTMEvent } from 'lib/utils/gtm-utils';
import componentMap from '.sitecore/component-map';
import { useTrackEntityPageView } from 'lib/hooks/search/useTrackEntityPageView';

const EXPERT_PROFILE_TEMPLATE_ID = 'e0ca334d-0e3d-4d41-9447-2b694b072fd7';
const NEWS_MEDIA_TEMPLATE_IDS = [
  '150f5c35-f575-4192-88b3-c24306edbb01',
  'bd73bac7-a401-4913-a42d-15238bba6baa',
];

const TAILWIND_VARIANTS = tv({
  slots: {
    footer: ['bg-color-surface-surface'],
    footerContentContainer: ['w-full'],
    main: ['font-typography-font-family-body', 'overflow-x-clip'],
  },
  variants: {
    pageEditing: {
      true: {
        main: ['editing-mode'],
      },
      false: {
        main: ['prod-mode'],
      },
    },
  },
});

interface LayoutProps {
  page: Page;
}

interface RouteFields {
  [key: string]: unknown;
  Title?: Field;
}

const Layout = ({ page }: LayoutProps): JSX.Element => {
  const { layout, mode } = page;
  const { route } = layout.sitecore;
  const fields = route?.fields as RouteFields;
  const isExpertProfile = route?.templateId === EXPERT_PROFILE_TEMPLATE_ID;
  const isNewsMediaArticle =
    !!route?.templateId && NEWS_MEDIA_TEMPLATE_IDS.includes(route.templateId);
  const pageForGtm = route as
    | (XceleratePage & {
        fields?: { schemaMarkup?: Field<string> };
        templateName: string;
      })
    | null
    | undefined;

  // Sitecore Search entity page views (required for CEC analytics / personalization).
  useTrackEntityPageView({
    itemId: route?.itemId,
    templateName: (route as { templateName?: string } | undefined)?.templateName,
    templateId: route?.templateId,
    enabled: Boolean(mode?.isNormal),
  });

  const site = layout.sitecore?.context?.site as { name?: string } | undefined;
  const siteSettings = layout.sitecore?.context?.siteSettings as
    | { gtmId?: { value?: string } | null }
    | undefined;
  const { gtmId } = siteSettings ?? {};
  const googleTagManagerId = gtmId?.value;

  const mainRef = useRef<HTMLDivElement>(null);
  const siteName = site?.name;
  const brand = getBrandForSiteName(siteName as SiteName);

  useOnRouteChange(() => {
    if (mainRef.current) {
      mainRef.current.focus({ preventScroll: true });
    }
  });

  /**
   * Last page reported to GTM, as `itemId|templateName`.
   *
   * `reactStrictMode` is on, so in development React mounts, runs effects, unmounts and
   * remounts — firing this effect twice and pushing two identical `content_view` events. Refs
   * survive that simulated remount, so recording what we already reported collapses it back to
   * one. A production build only ever runs the effect once, where this is a no-op.
   */
  const reportedContentViewRef = useRef<string | null>(null);

  useEffect(() => {
    if (!pageForGtm) return;

    const pageKey = `${route?.itemId ?? ''}|${pageForGtm.templateName ?? ''}`;
    if (reportedContentViewRef.current === pageKey) return;
    reportedContentViewRef.current = pageKey;

    const gtmEvent = createContentViewGTMEvent({
      contentType: pageForGtm.templateName || '',
    });
    sendGTMEvent({ ...gtmEvent });
  }, [pageForGtm, route?.itemId]);

  if (!googleTagManagerId) {
    console.warn('Google Tag Manager ID is missing. Google Analytics will not be rendered.');
  }

  const { footer, footerContentContainer, main } = TAILWIND_VARIANTS({
    pageEditing: !!mode.isEditing,
  });

  return (
    <>
      <Scripts />
      {/* Only render Schema on the published site, hide it from page builder */}
      {mode.isNormal && <Schema />}
      <SitecoreStyles layoutData={layout} />
      {process.env.NEXT_PUBLIC_ENABLE_SPEED_INSIGHTS === 'true' && <SpeedInsights />}
      {!route && (
        <ClientHead>
          <title>{fields?.Title?.value?.toString() || 'Page'}</title>
          <link rel="icon" href="/favicon.ico" />
        </ClientHead>
      )}

      <BrandAndThemeProvider brand={brand}>
        {googleTagManagerId ? <GoogleTagManager gtmId={googleTagManagerId} /> : null}
        <div className={main()} ref={mainRef} tabIndex={-1}>
          {mode.isDesignLibrary ? (
            <DesignLibrary />
          ) : (
            <>
              {route && (
                <PlaceholderWrapper
                  helpTextHideIf={true}
                  name="headless-header"
                  rendering={route}
                  page={page}
                  componentMap={componentMap}
                />
              )}
              <main>
                <div
                  id="content"
                  className={[
                    isExpertProfile ? 'blue-divider small-container' : '',
                    isNewsMediaArticle ? 'newsandmedia-article' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  {route && (
                    <PlaceholderWrapper
                      helpTextHideIf={true}
                      name="headless-main"
                      rendering={route}
                      page={page}
                      componentMap={componentMap}
                    />
                  )}
                </div>
              </main>
              <footer className={footer()}>
                <div className={footerContentContainer()}>
                  {route && (
                    <PlaceholderWrapper
                      helpTextHideIf={true}
                      name="headless-footer"
                      rendering={route}
                      page={page}
                      componentMap={componentMap}
                    />
                  )}
                </div>
              </footer>
              <BackToTop />
            </>
          )}
        </div>
      </BrandAndThemeProvider>
    </>
  );
};

export default Layout;
